/** The Lancer's own sounds (04 §5): its shot, its death, the next ship, a bonus ship. Pure. */

import { vary } from '../rand';
import { adsr, type VoiceSpec } from '../voice';
import { BASS, hit, LEAD, SECOND, type Sfx, tone, tune } from './kit';

/** One shot: a square zap falling almost two octaves in 70 ms, its top end rounded off. */
function zap(hz: number, at: number, level: number): VoiceSpec {
  return {
    ...hit('pulse', hz, 0.07, level),
    at,
    width: 0.5,
    to: hz * 0.3,
    glide: 0.07,
    filter: { type: 'lowpass', freq: 4200, q: 0 },
  };
}

/**
 * The most-heard sound in the game, so short and soft: under 120 ms, never quite the same pitch
 * twice. `n` is 1 for the dual fighter: the same zap doubled a fifth up, a hair later.
 */
export const fire: Sfx = (r, n) => {
  const hz = vary(r, 1500, 0.04);
  return n > 0 ? [zap(hz, 0, 0.19), zap(hz * 1.5, 0.012, 0.14)] : [zap(hz, 0, 0.22)];
};

/**
 * The fighter destroyed: a long burst of noise falling in pitch and brightness, a sawtooth growl
 * sliding down under it, and a thump. `n` is 1 when half of a dual fighter went and the other
 * flies on: the same blast, shorter and without the growl's long tail.
 */
export const playerDied: Sfx = (r, n) => {
  const long = n > 0 ? 0.55 : 1.2;
  return [
    {
      ...hit('noise', vary(r, 4200, 0.1), long, 0.7),
      to: 240,
      glide: long * 0.85,
      filter: { type: 'lowpass', freq: 7000, to: 200, sweep: long * 0.85, q: 4 },
    },
    {
      ...tone('sawtooth', vary(r, 196, 0.04), long * 0.75, adsr(0.005, long * 0.4, 0.5, 0.25), 0.26),
      to: 38,
      glide: long * 0.75,
      // Fast and wide enough to be roughness rather than a wobble: the growl.
      vibrato: { rate: 23, depth: 70, delay: 0 },
      filter: { type: 'lowpass', freq: 1800, to: 300, q: 3 },
    },
    { ...hit('triangle', 110, 0.3, 0.45), to: 30, glide: 0.3 },
  ];
};

/** READY, the next ship on the pad: two pick-up notes and a climb up the C major triad. */
export const ready: Sfx = () => [
  ...tune('G5 - G5 C6 . E6 . . .', 0.075, LEAD),
  ...tune('E5 - E5 G5 . C6 . . .', 0.075, SECOND),
  ...tune('C4 . . C4 . C5 . . .', 0.075, BASS),
];

/** A bonus fighter: a quick C major arpeggio up two octaves with a turn at the top, doubled below. */
export const extraLife: Sfx = () => [
  ...tune('C5 E5 G5 C6 E6 G6 C7 G6 C7 . . .', 0.055, LEAD),
  ...tune('C4 E4 G4 C5 E5 G5 C6 G5 C6 . . .', 0.055, { ...BASS, level: 0.2 }),
];
